import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Circle, MessageSquare, Radio, Send, RefreshCw, Trash2, Bell, Download } from "lucide-react";
import { Input } from "../components/ui/input";
import { fmt } from "../lib/api";
import { notifyUnread } from "../lib/adminAlert";
import {
  adminLive,
  adminProjects,
  adminProject,
  adminChats,
  adminReply,
  adminGetPresence,
  adminSetPresence,
  adminDeleteProject,
  adminDeleteProjects,
  adminGetCleanup,
  adminSetCleanup,
  adminGetNotifications,
  adminStats,
  adminSetNotifications,
  adminTestNotification,
  downloadProjectFile,
  downloadOrderFile,
  downloadProjectsList,
  confirmProjectOrder,
} from "../lib/projects";

const POLL_MS = 12000;

const ago = (s) => {
  if (s < 60) return `${s} secondi fa`;
  if (s < 3600) return `${Math.round(s / 60)} min fa`;
  return `${Math.round(s / 3600)} h fa`;
};

const dur = (s) => (s < 3600 ? `${Math.round(s / 60)} min` : `${Math.round(s / 3600)} h`);

const dot = (presence) =>
  presence === "online" ? "fill-[#4E7C59] text-[#4E7C59]" : presence === "inattivo" ? "fill-[#C9A227] text-[#C9A227]" : "fill-[#A8A29E] text-[#A8A29E]";

const TABS = [
  ["attivi", "Attivi ora"],
  ["salvati", "Salvati"],
  ["completati", "Completati"],
  ["inattivi", "Inattivi"],
];

const SessionCard = ({ s, onOpen, onChat, onDelete }) => (
  <div className="border border-border bg-card p-4" data-testid={`live-session-${s.code}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-2 label-xs">
        <Circle className={`h-2.5 w-2.5 ${dot(s.presence)}`} />
        {s.presence === "online" ? "Online ora" : s.presence === "inattivo" ? "Inattivo" : "Offline"}
      </span>
      <span className="font-mono-t text-xs text-[#4E7C59]">{s.saved ? s.code : "temporanea"}</span>
    </div>
    <div className="mt-2 font-display text-base">{s.title || s.name}</div>
    {!s.saved && (
      <div className="label-xs text-muted-foreground">Sessione anonima temporanea</div>
    )}
    <dl className="mt-3 space-y-1 text-sm">
      {[
        ["Fase", s.phase || "—"],
        ["Sta visualizzando", s.section || "—"],
        ["Ambienti", s.zones?.length ? s.zones.join(", ") : s.zone_count || "—"],
        ["Lavorazioni", s.item_count ? `${s.item_count} selezionate` : "—"],
        ["Stima attuale", s.net ? `€ ${fmt(s.net)} + IVA` : "non ancora disponibile"],
        ["Ultima attività", ago(s.last_seen_seconds)],
        ["Durata sessione", dur(s.session_seconds)],
      ].map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-border/60 pb-1">
          <dt className="label-xs text-muted-foreground">{k}</dt>
          <dd className="text-right">{v}</dd>
        </div>
      ))}
    </dl>
    <div className="mt-3 flex gap-2">
      <Button
        data-testid={`open-project-${s.code}`}
        size="sm"
        onClick={() => onOpen(s.id)}
        className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
      >
        Apri progetto
      </Button>
      <Button
        data-testid={`open-chat-${s.code}`}
        size="sm"
        variant="outline"
        onClick={() => onChat(s.id)}
        className="rounded-none"
      >
        <MessageSquare className="mr-1 h-3.5 w-3.5" /> Apri chat
        {s.unread_admin > 0 && (
          <span className="ml-1 bg-[#4E7C59] px-1.5 label-xs text-white">{s.unread_admin}</span>
        )}
      </Button>
      <Button
        data-testid={`live-delete-${s.code}`}
        size="sm"
        onClick={() => onDelete(s)}
        className="rounded-none bg-[#8A2E2E] text-white hover:bg-[#6B1F1F]"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  </div>
);

const ProjectDetail = ({ detail, onClose, onSend, token, onConfirmed }) => {
  const [text, setText] = useState("");
  const zones = detail?.state?.zones || [];
  const linesByZone = detail?.state?.lines || [];
  const dl = (fn, ok) =>
    fn()
      .then(() => toast.success(ok))
      .catch((e) => toast.error(e?.response?.data?.detail || "Download non riuscito"));
  return (
    <Dialog open={!!detail} onOpenChange={() => onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-2xl"
        data-testid="admin-project-detail"
      >
        {detail && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight">
                Progetto {detail.code} · {detail.title || detail.name}
              </DialogTitle>
            </DialogHeader>

            <div className="label-xs text-[#4E7C59]">Contatto cliente</div>
            {detail.quote_sent_at && (
              <div
                data-testid="detail-quote-sent"
                className="border-l-2 border-[#4E7C59] bg-[#4E7C59]/[0.08] px-3 py-2 text-sm"
              >
                PREVENTIVO INVIATO · {new Date(detail.quote_sent_at).toLocaleString("it-IT")}
              </div>
            )}

            {detail.client_editing && (
              <div
                data-testid="client-editing-warning"
                className="border-l-2 border-[#C9A227] bg-[#FFF8E1] px-3 py-2 text-sm"
              >
                IL CLIENTE STA MODIFICANDO QUESTO PROGETTO ORA — consultazione in sola lettura.
              </div>
            )}

            <div className="grid gap-1 text-sm">
              {[
                ["Cliente", detail.contact?.name || "non fornito"],
                ["Email", detail.contact?.email || "—"],
                ["Telefono", detail.contact?.phone || "—"],
                [
                  "Data invio preventivo",
                  detail.quote_sent_at
                    ? new Date(detail.quote_sent_at).toLocaleString("it-IT")
                    : "—",
                ],
                ["Codice progetto", detail.saved ? detail.code : "sessione temporanea"],
                ["Fase", detail.phase || "—"],
                ["Stato", detail.saved ? detail.status : "sessione temporanea"],
                ["Creato", new Date(detail.created_at).toLocaleString("it-IT")],
                ["Ultima modifica", new Date(detail.updated_at).toLocaleString("it-IT")],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-border/60 pb-1">
                  <span className="label-xs text-muted-foreground">{k}</span>
                  <span className="text-right">{v}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-y border-border py-3">
              <span className="label-xs text-muted-foreground">Esporta</span>
              {["xlsx", "ods", "csv"].map((f) => (
                <Button
                  key={f}
                  data-testid={`detail-export-${f}`}
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    dl(() => downloadProjectFile(token, detail.id, f), `File ${f.toUpperCase()} scaricato`)
                  }
                  className="rounded-none"
                >
                  <Download className="mr-1 h-3.5 w-3.5" /> {f.toUpperCase()}
                </Button>
              ))}
              {detail.status === "confermato" ? (
                <Button
                  data-testid="detail-export-order"
                  size="sm"
                  onClick={() => dl(() => downloadOrderFile(token, detail.id), "File ordine scaricato")}
                  className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
                >
                  <Download className="mr-1 h-3.5 w-3.5" /> Scarica file ordine
                </Button>
              ) : (
                <Button
                  data-testid="detail-confirm-order"
                  size="sm"
                  onClick={async () => {
                    try {
                      await confirmProjectOrder(token, detail.id);
                      toast.success("Progetto confermato: prezzi e quantità congelati");
                      onConfirmed?.(detail.id);
                    } catch {
                      toast.error("Conferma non riuscita");
                    }
                  }}
                  className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
                >
                  Conferma ordine
                </Button>
              )}
            </div>

            {zones.map((z) => {
              const zl = linesByZone.filter((l) => l.room_key === z.key);
              const d = z.dims || {};
              return (
                <div key={z.key} className="border border-border p-3">
                  <div className="font-display text-base">{z.name}</div>
                  <div className="label-xs text-muted-foreground">
                    {z.kind === "internal"
                      ? `${d.length || 0} × ${d.width || 0} × H ${d.height || 0} m`
                      : `${d.length || 0} × ${d.width || 0} m · area esterna`}
                  </div>
                  <div className="mt-2 space-y-1 text-sm">
                    {zl.length === 0 ? (
                      <div className="text-muted-foreground">Nessuna lavorazione selezionata</div>
                    ) : (
                      zl.map((l) => (
                        <div key={l.item_id} className="flex justify-between gap-3">
                          <span>{l.item_name}</span>
                          <span className="font-mono-t text-xs">
                            {l.qty} {l.unit} · € {fmt(l.total)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}

            <div className="space-y-1 border-t-2 border-[#1B2A20] pt-3 text-sm">
              <div className="flex justify-between">
                <span>Totale netto</span>
                <span className="font-mono-t">€ {fmt(detail.net)}</span>
              </div>
              <div className="flex justify-between">
                <span>IVA {detail.vat_rate}%</span>
                <span className="font-mono-t">€ {fmt(detail.vat)}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Totale</span>
                <span className="font-mono-t text-[#4E7C59]">€ {fmt(detail.total)}</span>
              </div>
            </div>

            <div>
              <div className="label-xs mb-2 text-[#4E7C59]">Conversazione</div>
              <div className="max-h-52 space-y-2 overflow-y-auto border border-border bg-[#F8FAFC] p-3">
                {(detail.messages || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun messaggio.</p>
                ) : (
                  detail.messages.map((m) => (
                    <div
                      key={m.id}
                      className={`max-w-[85%] border px-3 py-2 text-sm ${
                        m.sender === "bioedil"
                          ? "ml-auto border-[#4E7C59] bg-[#4E7C59]/[0.08]"
                          : "border-border bg-card"
                      }`}
                    >
                      <div className="label-xs mb-0.5 text-muted-foreground">
                        {m.sender === "bioedil" ? "BioEdil" : "Cliente"} ·{" "}
                        {new Date(m.created_at).toLocaleTimeString("it-IT", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                      {m.text}
                    </div>
                  ))
                )}
              </div>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
                <Textarea
                  data-testid="admin-chat-input"
                  rows={2}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Rispondi al cliente…"
                  className="flex-1 rounded-none"
                />
                <Button
                  data-testid="admin-chat-send"
                  onClick={async () => {
                    if (!text.trim()) return;
                    await onSend(detail.id, text.trim());
                    setText("");
                  }}
                  className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
                >
                  <Send className="mr-2 h-4 w-4" /> Rispondi
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AdminMonitor = ({ token }) => {
  const [live, setLive] = useState({ active_count: 0, sessions: [], unread_total: 0 });
  const [buckets, setBuckets] = useState({ attivi: [], salvati: [], completati: [], inattivi: [] });
  const [chats, setChats] = useState([]);
  const [tab, setTab] = useState("attivi");
  const [detail, setDetail] = useState(null);
  const [presence, setPresence] = useState(false);
  const [selected, setSelected] = useState({});
  const [since, setSince] = useState("");
  const [cleanup, setCleanup] = useState(0);
  const [notif, setNotif] = useState(null);
  const [stats, setStats] = useState(null);

  const refresh = async () => {
    try {
      const [l, p, c, pres, st] = await Promise.all([
        adminLive(token),
        adminProjects(token),
        adminChats(token),
        adminGetPresence(token),
        adminStats(token),
      ]);
      setLive(l);
      setBuckets(p);
      setChats(c.chats);
      setPresence(pres.flag);
      setStats(st);
      notifyUnread(l.unread_total);
    } catch {
      /* silenzioso: polling */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [cl, n] = await Promise.all([adminGetCleanup(token), adminGetNotifications(token)]);
        setCleanup(cl.days);
        setNotif(n);
      } catch {
        /* silenzioso */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveNotif = async (patch) => {
    const next = { ...notif, ...patch };
    setNotif(next);
    try {
      await adminSetNotifications(token, {
        email_enabled: next.email_enabled,
        email: next.email,
        notify_new_message: next.notify_new_message,
        notify_first_use: next.notify_first_use,
        only_when_offline: next.only_when_offline,
        skip_if_admin_open: next.skip_if_admin_open,
      });
    } catch {
      toast.error("Salvataggio notifiche non riuscito");
    }
  };

  const visibleProjects = (buckets[tab] || []).filter(
    (p) => !since || (p.last_seen || "") <= new Date(since).toISOString()
  );

  const removeOne = async (p) => {
    const extra = p.saved
      ? "\n\nATTENZIONE: questo progetto è stato salvato dal cliente e il suo link non funzionerà più."
      : "";
    if (
      !window.confirm(
        "Vuoi eliminare definitivamente questo progetto? Verranno eliminati anche configurazione, chat e dati associati." +
          extra
      )
    )
      return;
    try {
      await adminDeleteProject(token, p.id);
      toast.success("Progetto eliminato");
      setSelected((s) => ({ ...s, [p.id]: false }));
      refresh();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const removeSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast.error("Nessun progetto selezionato");
    if (
      !window.confirm(
        `Vuoi eliminare definitivamente ${ids.length} progetti? Verranno eliminati anche configurazione, chat e dati associati.`
      )
    )
      return;
    try {
      const res = await adminDeleteProjects(token, ids);
      toast.success(`${res.deleted} progetti eliminati`);
      setSelected({});
      refresh();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(() => {
      if (!document.hidden) refresh();
    }, POLL_MS);
    const onVisible = () => !document.hidden && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openProject = async (id) => {
    try {
      setDetail(await adminProject(token, id));
      refresh();
    } catch {
      toast.error("Progetto non disponibile");
    }
  };

  const reply = async (id, text) => {
    try {
      await adminReply(token, id, text);
      setDetail(await adminProject(token, id));
      refresh();
      toast.success("Risposta inviata");
    } catch {
      toast.error("Invio non riuscito");
    }
  };

  const togglePresence = async () => {
    try {
      const res = await adminSetPresence(token, !presence);
      setPresence(res.online);
    } catch {
      toast.error("Aggiornamento stato non riuscito");
    }
  };

  return (
    <>
      {stats && (
        <section data-testid="admin-stats-section" className="grid gap-4 md:grid-cols-2">
          <div className="border border-border bg-card p-5">
            <h3 className="font-display text-base uppercase tracking-tight">Visite configuratore</h3>
            <dl className="mt-3 space-y-1 text-sm">
              {[
                ["Oggi", stats.visits.today, "stat-visits-today"],
                ["Ieri", stats.visits.yesterday, "stat-visits-yesterday"],
                ["Ultimi 7 giorni", stats.visits.last7, "stat-visits-last7"],
              ].map(([k, v, tid]) => (
                <div key={k} className="flex justify-between border-b border-border/60 pb-1">
                  <dt className="label-xs text-muted-foreground">{k}</dt>
                  <dd data-testid={tid} className="font-mono-t">
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="border border-border bg-card p-5">
            <h3 className="font-display text-base uppercase tracking-tight">Attività</h3>
            <dl className="mt-3 space-y-1 text-sm">
              {[
                ["Nuovi progetti", stats.activity.new_projects, "stat-new-projects"],
                ["Preventivi inviati", stats.activity.quotes_sent, "stat-quotes-sent"],
                ["Messaggi non letti", stats.activity.unread_messages, "stat-unread"],
              ].map(([k, v, tid]) => (
                <div key={k} className="flex justify-between border-b border-border/60 pb-1">
                  <dt className="label-xs text-muted-foreground">{k}</dt>
                  <dd data-testid={tid} className="flex items-center gap-2 font-mono-t">
                    {v}
                    {v > 0 && <span className="bg-[#4E7C59] px-1.5 label-xs text-white">NUOVO</span>}
                  </dd>
                </div>
              ))}
            </dl>
            {stats.provider_configured === false && (
              <p data-testid="stat-provider-warning" className="mt-3 label-xs text-[#8A2E2E]">
                Provider email non configurato
              </p>
            )}
          </div>
        </section>
      )}

      <section data-testid="admin-live-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
            Configuratore live
          </h2>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-presence-toggle"
              size="sm"
              onClick={togglePresence}
              className={`rounded-none ${presence ? "bg-[#4E7C59] text-white" : "bg-[#57534E] text-white"}`}
            >
              <Radio className="mr-2 h-3.5 w-3.5" />
              {presence ? "BioEdil online" : "BioEdil offline"}
            </Button>
            <Button
              data-testid="admin-live-refresh"
              size="sm"
              variant="outline"
              onClick={refresh}
              className="rounded-none"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <p className="mt-1 label-xs text-muted-foreground">
          <span data-testid="admin-live-count">{live.active_count}</span> utenti attivi ·{" "}
          <span data-testid="admin-unread-total">{live.unread_total}</span> messaggi non letti ·
          aggiornamento automatico
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {live.sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nessuna sessione attiva in questo momento.
            </p>
          ) : (
            live.sessions.map((s) => (
              <SessionCard key={s.id} s={s} onOpen={openProject} onChat={openProject} onDelete={removeOne} />
            ))
          )}
        </div>
      </section>

      <section data-testid="admin-projects-section">
        <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">Progetti</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`projects-tab-${key}`}
              onClick={() => setTab(key)}
              className={`border px-3 py-1.5 label-xs transition-colors ${
                tab === key
                  ? "border-[#1B2A20] bg-[#1B2A20] text-[#F4F7F2]"
                  : "border-border hover:border-[#4E7C59]"
              }`}
            >
              {label} ({buckets[key]?.length || 0})
            </button>
          ))}
        </div>
        <div className="mt-4 border border-border bg-card" data-testid="admin-projects-table">
          <div className="flex flex-wrap items-end gap-3 border-b border-border bg-[#F3F6F1] px-4 py-3">
            <label className="block">
              <span className="label-xs text-muted-foreground">
                Ultima attività precedente al
              </span>
              <Input
                data-testid="inactive-date-filter"
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
                className="mt-1 w-44 rounded-none text-sm"
              />
            </label>
            <Button
              data-testid="delete-selected-projects"
              size="sm"
              onClick={removeSelected}
              className="rounded-none bg-[#8A2E2E] text-white hover:bg-[#6B1F1F]"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina selezionati
            </Button>
            <Button
              data-testid="export-selected-projects"
              size="sm"
              onClick={() => {
                const ids = Object.keys(selected).filter((k) => selected[k]);
                if (!ids.length) return toast.error("Nessun progetto selezionato");
                downloadProjectsList(token, { ids })
                  .then(() => toast.success("File riepilogativo scaricato"))
                  .catch(() => toast.error("Download non riuscito"));
              }}
              className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
            >
              <Download className="mr-1 h-3.5 w-3.5" /> Esporta selezionati
            </Button>
            <span className="label-xs text-muted-foreground">
              {visibleProjects.length} progetti in questa vista
            </span>
          </div>
          {visibleProjects.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground">Nessun progetto in questa vista.</p>
          ) : (
            visibleProjects.map((p) => (
              <div
                key={p.id}
                className="grid gap-2 border-b border-border px-4 py-3 text-sm last:border-0 sm:grid-cols-[30px_110px_1fr_130px_110px_110px_140px]"
              >
                <span className="flex items-center">
                  <input
                    data-testid={`project-select-${p.id}`}
                    type="checkbox"
                    checked={!!selected[p.id]}
                    onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))}
                    className="h-4 w-4 accent-[#1B2A20]"
                  />
                </span>
                <span className="font-mono-t text-xs text-[#4E7C59]">
                  {p.saved ? p.code : "temp."}
                </span>
                <span>
                  {p.contact?.name ? `${p.contact.name} – ` : ""}
                  {p.title || p.name}
                  {p.admin_new && (
                    <span
                      data-testid={`badge-new-${p.id}`}
                      className="ml-2 bg-[#4E7C59] px-1.5 label-xs text-white"
                    >
                      NUOVO
                    </span>
                  )}
                  {p.quote_sent_at && (
                    <span
                      data-testid={`badge-quote-${p.id}`}
                      className="ml-2 bg-[#1B2A20] px-1.5 label-xs text-[#F4F7F2]"
                    >
                      PREVENTIVO
                    </span>
                  )}
                  <span className="ml-2 label-xs text-muted-foreground">{p.phase}</span>
                </span>
                <span className="label-xs text-muted-foreground">
                  {p.zone_count} ambienti · {p.item_count} lavor.
                </span>
                <span className="font-mono-t text-xs">€ {fmt(p.net)} netto</span>
                <span className="font-mono-t text-xs">
                  IVA {p.vat_rate}% · € {fmt(p.total)}
                </span>
                <span className="flex items-center justify-end gap-2">
                  {p.unread_admin > 0 && (
                    <span className="bg-[#4E7C59] px-1.5 label-xs text-white">
                      {p.unread_admin}
                    </span>
                  )}
                  <Button
                    data-testid={`project-open-${p.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => openProject(p.id)}
                    className="rounded-none"
                  >
                    Apri
                  </Button>
                  <Button
                    data-testid={`project-delete-${p.id}`}
                    size="sm"
                    onClick={() => removeOne(p)}
                    className="rounded-none bg-[#8A2E2E] text-white hover:bg-[#6B1F1F]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      <section data-testid="admin-chats-section">
        <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
          Chat clienti{" "}
          {live.unread_total > 0 && (
            <span className="bg-[#4E7C59] px-2 py-0.5 align-middle label-xs text-white">
              {live.unread_total} non letti
            </span>
          )}
        </h2>
        <div className="mt-4 border border-border bg-card">
          {chats.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground">Nessuna conversazione.</p>
          ) : (
            chats.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 text-sm last:border-0"
                data-testid={`admin-chat-row-${c.id}`}
              >
                <span className="min-w-[220px] flex-1">
                  <span className="font-mono-t text-xs text-[#4E7C59]">
                    {c.saved ? c.code : "temp."}
                  </span>{" "}
                  {c.title || c.name}
                  <span className="mt-0.5 block label-xs text-muted-foreground">
                    Sta configurando: {c.section || "—"} · {c.item_count} lavorazioni · € {fmt(c.net)}{" "}
                    + IVA
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    “{c.last_message?.text?.slice(0, 90) || ""}”
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  {c.unread_admin > 0 && (
                    <span className="bg-[#4E7C59] px-1.5 label-xs text-white">
                      {c.unread_admin} nuovi
                    </span>
                  )}
                  <Button
                    data-testid={`admin-chat-open-${c.id}`}
                    size="sm"
                    onClick={() => openProject(c.id)}
                    className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
                  >
                    Vedi progetto e rispondi
                  </Button>
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      <section data-testid="admin-notifications-section">
        <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
          Notifiche assistenza
        </h2>
        <p className="mt-1 label-xs text-muted-foreground">
          Notifica email all'arrivo di un nuovo messaggio dal configuratore. L'email contiene solo
          il testo del messaggio e il link alla chat nel pannello Admin.
        </p>
        {notif && (
          <div className="mt-4 grid gap-4 border border-border bg-card p-5 md:grid-cols-2">
            <label className="flex items-center gap-3 text-sm">
              <input
                data-testid="notif-email-enabled"
                type="checkbox"
                checked={notif.email_enabled}
                onChange={(e) => saveNotif({ email_enabled: e.target.checked })}
                className="h-4 w-4 accent-[#1B2A20]"
              />
              Notifica email attiva
            </label>
            <label className="block">
              <span className="label-xs text-muted-foreground">Email account Admin</span>
              <Input
                data-testid="notif-email"
                value={notif.email}
                onChange={(e) => setNotif({ ...notif, email: e.target.value })}
                onBlur={() => saveNotif({ email: notif.email })}
                placeholder="admin@bioedilabruzzo.it"
                className="mt-1 rounded-none font-mono-t text-sm"
              />
            </label>
            <label className="flex items-center gap-3 text-sm">
              <input
                data-testid="notif-new-message"
                type="checkbox"
                checked={notif.notify_new_message}
                onChange={(e) => saveNotif({ notify_new_message: e.target.checked })}
                className="h-4 w-4 accent-[#1B2A20]"
              />
              Notifica nuovo messaggio
            </label>
            <label className="flex items-center gap-3 text-sm">
              <input
                data-testid="notif-first-use"
                type="checkbox"
                checked={notif.notify_first_use !== false}
                onChange={(e) => saveNotif({ notify_first_use: e.target.checked })}
                className="h-4 w-4 accent-[#1B2A20]"
              />
              Notifica nuovo utilizzo (una per sessione)
            </label>
            <label className="flex items-center gap-3 text-sm">
              <input
                data-testid="notif-only-offline"
                type="checkbox"
                checked={notif.only_when_offline}
                onChange={(e) => saveNotif({ only_when_offline: e.target.checked })}
                className="h-4 w-4 accent-[#1B2A20]"
              />
              Solo quando BioEdil è offline
            </label>
            <label className="flex items-center gap-3 text-sm">
              <input
                data-testid="notif-skip-admin-open"
                type="checkbox"
                checked={!!notif.skip_if_admin_open}
                onChange={(e) => saveNotif({ skip_if_admin_open: e.target.checked })}
                className="h-4 w-4 accent-[#1B2A20]"
              />
              Non inviare se il pannello Admin è aperto
            </label>
            <div className="md:col-span-2 flex flex-wrap items-center gap-3">
              <Button
                data-testid="notif-test"
                onClick={async () => {
                  try {
                    const res = await adminTestNotification(token);
                    res.sent
                      ? toast.success("Email di test inviata")
                      : toast.error(res.reason || "Invio non riuscito");
                  } catch {
                    toast.error("Test non riuscito");
                  }
                }}
                className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
              >
                <Bell className="mr-2 h-4 w-4" /> Invia email di test
              </Button>
              <span
                data-testid="notif-provider-status"
                className="label-xs text-muted-foreground"
              >
                {notif.provider_configured
                  ? "Provider email configurato"
                  : "Provider email non configurato: chat e configuratore funzionano normalmente"}
              </span>
            </div>
            <label className="block md:col-span-2">
              <span className="label-xs text-muted-foreground">
                Pulizia automatica sessioni anonime non salvate
              </span>
              <select
                data-testid="cleanup-select"
                value={cleanup}
                onChange={async (e) => {
                  const days = Number(e.target.value);
                  setCleanup(days);
                  try {
                    await adminSetCleanup(token, days);
                    toast.success("Impostazione salvata");
                  } catch {
                    toast.error("Salvataggio non riuscito");
                  }
                }}
                className="mt-1 block border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-[#4E7C59]"
              >
                <option value={0}>Mai</option>
                <option value={7}>Dopo 7 giorni</option>
                <option value={30}>Dopo 30 giorni</option>
                <option value={60}>Dopo 60 giorni</option>
                <option value={90}>Dopo 90 giorni</option>
              </select>
              <span className="mt-1 block label-xs text-muted-foreground">
                Riguarda solo le sessioni anonime non salvate e realmente inattive. I progetti
                salvati dal cliente non vengono mai eliminati automaticamente.
              </span>
            </label>
          </div>
        )}
      </section>

      <ProjectDetail
        detail={detail}
        onClose={() => setDetail(null)}
        onSend={reply}
        token={token}
        onConfirmed={async (id) => {
          setDetail(await adminProject(token, id));
          refresh();
        }}
      />
    </>
  );
};
